import { jsmUseCaseProvisioningMessages as messages } from './jsm-use-case-provisioning-messages';

import type { JsmIncidentManagementSetupState } from './jsm-incident-management-setup-state';
import type { JsmIncidentManagementSetupError } from './run-jsm-incident-management-setup';
import type { JsmUseCaseProjectStatus } from './use-jsm-use-case-project-create';
import type { MessageDescriptor } from 'react-intl';

const stageMessages: Partial<Record<JsmIncidentManagementSetupState['stage'], MessageDescriptor>> =
	{
		'checking-organization': messages.checkingOrganizationStatus,
		'creating-team': messages.creatingOperationsTeamStatus,
		'checking-membership': messages.checkingTeamMembershipStatus,
		'checking-eligibility': messages.checkingOperationsAccessStatus,
		enabling: messages.enablingOperationsStatus,
		polling: messages.finishingOperationsStatus,
	};

const getOperationsErrorMessage = (failure: JsmIncidentManagementSetupError | undefined) =>
	failure?.reason === 'permission' || failure?.apiError?.kind === 'permission'
		? messages.incidentOperationsPermissionError
		: messages.incidentOperationsError;

export const getJsmIncidentManagementFeedback = ({
	isIncidentManagement,
	projectStatus,
	incidentStatus,
	failure,
}: {
	readonly isIncidentManagement: boolean;
	readonly projectStatus: JsmUseCaseProjectStatus;
	readonly incidentStatus: JsmIncidentManagementSetupState['stage'];
	readonly failure?: JsmIncidentManagementSetupError;
}): { readonly message?: MessageDescriptor; readonly canRecover: boolean } => {
	if (!isIncidentManagement) return { canRecover: false };
	if (failure && ['scope-mismatch', 'interrupted'].includes(failure.reason)) {
		return { message: messages.incidentContextChanged, canRecover: false };
	}
	if (projectStatus === 'error')
		return { message: messages.incidentProjectError, canRecover: false };
	if (projectStatus === 'timeout')
		return { message: messages.incidentProjectTimeout, canRecover: false };
	if (projectStatus !== 'complete' || incidentStatus === 'complete') return { canRecover: false };
	if (incidentStatus === 'timeout' || incidentStatus === 'error') {
		return {
			message:
				incidentStatus === 'timeout'
					? messages.incidentOperationsTimeout
					: getOperationsErrorMessage(failure),
			canRecover: true,
		};
	}
	return {
		message: stageMessages[incidentStatus] ?? messages.preparingOperationsStatus,
		canRecover: false,
	};
};
